/**
 * PRP-WEB-2 review W5: leaving the floor plan with unsaved edits asks first, whatever the way out.
 *
 * The app mounts a plain `<BrowserRouter>`, and react-router 6's `useBlocker` works only under a
 * data router (`createBrowserRouter`), so the blocker is built on the router's own navigator
 * instead (`UNSAFE_NavigationContext`): while the plan is dirty its `push`/`replace` ask before
 * they run. That is every in-app navigation, a `<Link>` and a `navigate()` alike (the top bar's
 * project switcher, `V2TopBar`), with no change to the callers.
 *
 * Browser Back: while dirty a duplicate of the current history entry sits on top, so Back lands on
 * the same page and the router keeps it mounted; it then asks. Leave goes back once more for real;
 * Stay puts the duplicate back. Tab close and reload get the browser's own `beforeunload` prompt.
 */
import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

export const LEAVE_MESSAGE = 'You have unsaved changes to this floor plan. Leave without saving?';
const GUARD = '__planLeaveGuard';

type Nav = { push: (...a: unknown[]) => void; replace: (...a: unknown[]) => void };

const guarded = (): boolean => {
  const s = window.history.state as Record<string, unknown> | null;
  return !!s && s[GUARD] === true;
};

export function useLeaveGuard(dirty: boolean, message: string = LEAVE_MESSAGE): void {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const leaving = useRef(false);

  // In-app navigation: Link, navigate(), the project switcher.
  useEffect(() => {
    if (!dirty) return undefined;
    const nav = navigator as unknown as Nav;
    const push = nav.push;
    const replace = nav.replace;
    const ask = (go: (...a: unknown[]) => void) => (...a: unknown[]) => {
      if (leaving.current || window.confirm(message)) {
        leaving.current = true;
        go.apply(nav, a);
        setTimeout(() => { leaving.current = false; }, 0);   // a same-page navigate asks again next time
      }
    };
    nav.push = ask(push);
    nav.replace = ask(replace);
    return () => { nav.push = push; nav.replace = replace; };
  }, [dirty, navigator, message]);

  // Tab close / reload.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // Browser Back.
  useEffect(() => {
    if (!dirty) return undefined;
    leaving.current = false;
    const arm = () => {
      if (!guarded()) window.history.pushState({ ...(window.history.state ?? {}), [GUARD]: true }, '', window.location.href);
    };
    arm();
    const onPop = () => {
      if (leaving.current) return;
      if (window.confirm(message)) {
        leaving.current = true;
        window.history.back();
      } else {
        arm();
      }
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Saved (no longer dirty) on this page: take the duplicate entry back off the stack.
      if (!leaving.current && guarded()) {
        leaving.current = true;
        window.history.back();
        setTimeout(() => { leaving.current = false; }, 0);
      }
    };
  }, [dirty, message]);
}

export default useLeaveGuard;
